<script setup>
const componentProps = defineProps({
  modelValue: {
    type: [Number, Object],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  popUpLabel: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  error: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: [String, Array],
    required: false,
    default: null,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
  disable: {
    type: Boolean,
    required: false,
    default: false,
  },

  range: {
    type: Boolean,
    required: false,
    default: false,
  },
  min: {
    type: Number,
    required: false,
    default: undefined,
  },
  max: {
    type: Number,
    required: false,
    default: undefined,
  },
  step: {
    type: Number,
    required: false,
    default: undefined,
  },
  dense: {
    type: Boolean,
    required: false,
    default: false,
  },
  snaps: {
    type: Boolean,
    required: false,
    default: false,
  },
  markers: {
    type: [Boolean, Number],
    required: false,
    default: false,
  },
  markerLabels: {
    type: [Boolean, Array, Object, Function],
    required: false,
    default: false,
  },
  markerLabelsClass: {
    type: String,
    required: false,
    default: null,
  },
  vertical: {
    type: Boolean,
    required: false,
    default: false,
  },

  color: {
    type: String,
    required: false,
    default: 'primary',
  },
  trackColor: {
    type: String,
    required: false,
    default: undefined,
  },
  thumbColor: {
    type: String,
    required: false,
    default: undefined,
  },

  labelAlways: {
    type: Boolean,
    required: false,
    default: false,
  },
  labelValue: {
    type: [String, Number],
    required: false,
    default: null,
  },

  prefix: {
    type: String,
    required: false,
    default: '',
  },
  suffix: {
    type: String,
    required: false,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change'])

/**
 * Internal computed v-model binding so we can re-emit update:modelValue.
 */
const innerValue = computed({
  get: () => componentProps.modelValue,
  set: (val) => emit('update:modelValue', val),
})
const minValue = computed(() => componentProps.min ?? 0)
const maxValue = computed(() => componentProps.max ?? 100)
const stepValue = computed(() => componentProps.step ?? 10)

/**
 * Computed label value used for the thumb label.
 * - Single value: "<prefix><value><suffix>"
 * - Range: "<prefix>min - max<suffix>"
 */
const computedLabelValue = computed(() => {
  if (componentProps.labelValue !== null && typeof componentProps.labelValue !== 'undefined') {
    return componentProps.labelValue
  }

  const value = innerValue.value

  if (componentProps.range && value && typeof value === 'object') {
    return `${componentProps.prefix}${value.min} - ${value.max}${componentProps.suffix}`
  }

  if (typeof value === 'number') {
    return `${componentProps.prefix}${value}${componentProps.suffix}`
  }

  return value
})
</script>

<template>
  <q-field outlined :label="label" :stack-label="!!label" v-bind="$attrs" :error="error" :error-message="errorMessage" :readonly="readonly">
    <q-range
      v-if="range"
      class="q-px-xs"
      v-model="innerValue"
      :label="popUpLabel"
      :label-always="labelAlways"
      :label-value="computedLabelValue"
      :min="minValue"
      :max="maxValue"
      :step="stepValue"
      :color="color"
      :track-color="trackColor"
      :thumb-color="thumbColor"
      :dense="dense"
      :snap="snaps"
      :markers="markers"
      :marker-labels="markerLabels"
      :marker-labels-class="markerLabelsClass"
      :vertical="vertical"
      :readonly="readonly"
      :disable="disable || readonly"
      @change="$emit('change', innerValue)"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
    />

    <q-slider
      v-else
      v-model="innerValue"
      class="q-px-xs"
      :label="popUpLabel"
      :label-always="labelAlways"
      :label-value="computedLabelValue"
      :min="minValue"
      :max="maxValue"
      :step="stepValue"
      :color="color"
      :track-color="trackColor"
      :thumb-color="thumbColor"
      :dense="dense"
      :snap="snaps"
      :markers="markers"
      :marker-labels="markerLabels"
      :marker-labels-class="markerLabelsClass"
      :vertical="vertical"
      :readonly="readonly"
      :disable="disable || readonly"
      @change="$emit('change', innerValue)"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
    />
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}"></slot>
    </template>
  </q-field>
</template>
